import { usePageTitle } from '@/hooks/usePageTitle';
import PortalNav from '@/components/PortalNav';
import PortalFooter from '@/components/PortalFooter';
import { portalPageStyles } from '@/styles/portalPageStyles';

const sectionStyle: React.CSSProperties = {
  marginBottom: '2rem',
};

const headingStyle: React.CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: 600,
  color: 'var(--text-primary)',
  marginBottom: '1rem',
  marginTop: 0,
};

const subHeadingStyle: React.CSSProperties = {
  fontSize: '1.15rem',
  fontWeight: 600,
  color: 'var(--text-primary)',
  marginBottom: '0.75rem',
  marginTop: '1.25rem',
};

const paragraphStyle: React.CSSProperties = {
  fontSize: '0.95rem',
  color: 'var(--text-secondary)',
  lineHeight: 1.7,
  marginBottom: '0.75rem',
};

const listStyle: React.CSSProperties = {
  fontSize: '0.95rem',
  color: 'var(--text-secondary)',
  lineHeight: 1.7,
  paddingLeft: '1.5rem',
  marginBottom: '0.75rem',
};

const sampleBoxStyle: React.CSSProperties = {
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-color)',
  borderRadius: '0.5rem',
  padding: '0.75rem 1rem',
  fontFamily: 'monospace',
  fontSize: '0.85rem',
  color: 'var(--text-primary)',
  marginBottom: '0.5rem',
};

function SmsTermsPage() {
  usePageTitle('SMS Terms');

  return (
    <div style={portalPageStyles.pageContainer}>
      <PortalNav />

      {/* Content */}
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2.5rem 2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
          SMS Terms
        </h1>
        <p style={{ ...paragraphStyle, marginBottom: '2rem' }}>
          Last updated: February 12, 2026
        </p>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Program Name</h2>
          <p style={paragraphStyle}>Checkademic SMS Notifications</p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Program Description</h2>
          <p style={paragraphStyle}>
            Checkademic SMS Notifications delivers brief behavior and attendance digest summaries
            via text message to opted-in parents and guardians of K-12 students. Messages include
            a count of behavior records and absences for the digest period, along with a link to the
            family portal for full details.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Message Frequency</h2>
          <p style={paragraphStyle}>
            Message frequency varies based on your selected digest preferences:
          </p>
          <ul style={listStyle}>
            <li>Daily digest: Up to 1 message per day</li>
            <li>Weekly digest: Up to 1 message per week</li>
          </ul>
          <p style={paragraphStyle}>
            Messages are only sent when there is activity to report (behaviors or absences recorded
            for your linked student during the digest period).
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Message and Data Rates</h2>
          <p style={paragraphStyle}>
            Message and data rates may apply. Contact your wireless carrier for details about your
            text messaging plan.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Opt-In Process</h2>
          <p style={paragraphStyle}>
            Parents and guardians opt in to SMS notifications through the following steps.
            See the{' '}
            <a href="/sms-opt-in" style={{ color: 'var(--primary-color)' }}>SMS Opt-In Process</a>
            {' '}page for screenshots of each step.
          </p>
          <ol style={listStyle}>
            <li>A school administrator creates a parent account or sends an invitation link.</li>
            <li>
              The parent creates their account and provides their phone number during registration.
              At the phone number field, a disclosure states: "Used for SMS digest notifications.
              Msg &amp; data rates may apply." with a link to this SMS Terms page.
            </li>
            <li>After logging in, the parent navigates to Notification Settings.</li>
            <li>
              On the Notification Settings page, the parent toggles "SMS Notifications" to enabled.
              A disclosure is displayed: "By enabling SMS, you consent to receive up to 1 message
              per day (daily digest) or 1 per week (weekly digest). Msg &amp; data rates may apply.
              Reply STOP to cancel, HELP for help." with links to this SMS Terms page and the Privacy Policy.
            </li>
            <li>The parent selects their preferred digest frequency (daily, weekly, or both).</li>
          </ol>
          <p style={paragraphStyle}>
            SMS is not required to use Checkademic. All notifications are also available via email.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Opt-Out Instructions</h2>
          <p style={paragraphStyle}>
            You can opt out of SMS notifications at any time using either method:
          </p>
          <ul style={listStyle}>
            <li>Reply <strong>STOP</strong> to any message to cancel all SMS notifications.</li>
            <li>Log in to the family portal, go to Notification Settings, and toggle SMS off.</li>
          </ul>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Help</h2>
          <p style={paragraphStyle}>
            For help with SMS notifications:
          </p>
          <ul style={listStyle}>
            <li>Reply <strong>HELP</strong> to any message for assistance.</li>
            <li>
              Email us at{' '}
              <a href="mailto:contact@checkademic.com" style={{ color: 'var(--primary-color)' }}>
                contact@checkademic.com
              </a>
            </li>
          </ul>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Sample Messages</h2>
          <p style={paragraphStyle}>
            Below are examples of the SMS digest messages you may receive:
          </p>

          <h3 style={subHeadingStyle}>Daily digest (with recognitions and incidents)</h3>
          <div style={sampleBoxStyle}>
            [Soda Springs HS] 24h for Emma: 1 recognition, 1 incident, 1 absence. Details: sshs.checkademic.com/parent
          </div>

          <h3 style={subHeadingStyle}>Weekly digest (incidents only)</h3>
          <div style={sampleBoxStyle}>
            [Tigert Middle] Week for Jacob: 5 incidents. Details: tms.checkademic.com/parent
          </div>

          <h3 style={subHeadingStyle}>Daily digest (recognitions only)</h3>
          <div style={sampleBoxStyle}>
            [Thirkill Elem] 24h for Sophia: 1 recognition. Details: tes.checkademic.com/parent
          </div>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Related Policies</h2>
          <ul style={listStyle}>
            <li>
              <a href="/privacy" style={{ color: 'var(--primary-color)' }}>Privacy Policy</a>
              {' — '}How we collect, use, and protect your information
            </li>
            <li>
              <a href="/terms" style={{ color: 'var(--primary-color)' }}>Terms of Service</a>
              {' — '}Terms governing use of Checkademic
            </li>
            <li>
              <a href="/sms-opt-in" style={{ color: 'var(--primary-color)' }}>SMS Opt-In Process</a>
              {' — '}How parents opt in to SMS notifications (with screenshots)
            </li>
          </ul>
        </div>
      </div>

      {/* Footer */}
      <PortalFooter currentPage="sms-terms" />
    </div>
  );
}

export default SmsTermsPage;
